import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    eyebrow: "Sustainability Report 2026",
    title: "Energising today,",
    titleItalic: "Advancing tomorrow.",
    body: "",
    cta: { label: "Download the report", to: "/about" },
    image:
      "https://images.unsplash.com/photo-1753771145085-d71e89c23092",
  },
  {
    eyebrow: "Annual Report 2026",
    title: "A year of",
    titleItalic: "disciplined growth.",
    body: "Multi-year offtake agreements signed across Europe, Asia and the Middle East.",
    cta: { label: "Download the report", to: "/about" },
    image:
      "https://images.unsplash.com/photo-1753771145110-b3ddcd1b0ae2",
  },
  {
    eyebrow: "Our People",
    title: "Meet the team that",
    titleItalic: "powers our operations.",
    body: "From our Kabwe headquarters and export yard — the people behind EELLEVEN MINING.",
    cta: { label: "Read more", to: "/about" },
    image:
      "https://images.unsplash.com/photo-1664565262236-65166affe34a",
  },
];

export default function HeroCarousel() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % SLIDES.length);
    }, 7000);
    return () => clearInterval(id);
  }, []);

  const slide = SLIDES[active];

  return (
    <section
      className="relative h-[78vh] min-h-[560px] w-full overflow-hidden"
      data-testid="hero-carousel"
    >
      {SLIDES.map((s, i) => (
        <img
          key={i}
          src={s.image}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--eel-bg)] via-[var(--eel-bg)]/85 to-[var(--eel-bg)]/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--eel-bg)]" />

      <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
        <div key={active} className="reveal">
          <p
            className="label-eyebrow text-[var(--eel-copper)]"
            data-testid="hero-eyebrow"
          >
            {slide.eyebrow}
          </p>
          <h1
            data-testid="hero-title"
            className="font-editorial text-5xl md:text-7xl lg:text-[88px] mt-6 leading-[0.98] max-w-4xl text-white"
          >
            {slide.title}
            <br />
            <span className="font-editorial-italic copper-text">
              {slide.titleItalic}
            </span>
          </h1>
          {slide.body && (
            <p
              className="mt-7 max-w-xl text-base md:text-lg text-zinc-200 leading-relaxed font-light"
              data-testid="hero-body"
            >
              {slide.body}
            </p>
          )}
          <Link
            to={slide.cta.to}
            data-testid="hero-cta"
            className="mt-9 inline-flex items-center gap-2 text-sm uppercase tracking-widest copper-text link-underline"
          >
            {slide.cta.label} <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-5 z-10">
        <button
          aria-label="Previous slide"
          data-testid="hero-prev"
          onClick={() =>
            setActive((p) => (p - 1 + SLIDES.length) % SLIDES.length)
          }
          className="w-10 h-10 grid place-items-center text-white/70 hover:text-white border border-white/20 hover:border-white/60 transition"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              onClick={() => setActive(i)}
              className={`h-1 transition-all ${
                i === active
                  ? "w-10 copper-bg"
                  : "w-6 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
        <button
          aria-label="Next slide"
          data-testid="hero-next"
          onClick={() => setActive((p) => (p + 1) % SLIDES.length)}
          className="w-10 h-10 grid place-items-center text-white/70 hover:text-white border border-white/20 hover:border-white/60 transition"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </section>
  );
}
